import { IFacet } from '../models/IFacet';
import { getSearchResults, SearchApiSettings } from '../api-clients/search-api-client';
import { useAsyncData } from './use-async-data';
import { DependencyList } from 'react';

export type SearchHookProps = {
  keyword?: string;
  facets?: IFacet[];
  sortOrder?: string;
  page?: number;
  pageSize?: number;
  userToken?: string;
  searchApiSettings: SearchApiSettings;
  // brandId is for getting brand specific course data from searchService.
  brandId?: string;
};

const keywordParamName = `keyword`;
const facetTermsParamName = `facetTerms`;
const sortOrderParamName = `sortOrder`;
const pageSizeParamName = `pageSize`;
const pageParamName = `page`;
const userTokenParamName = `userToken`;

export function getSearchQuery(searchOptions: SearchHookProps) {
  const queryParts: string[] = [];

  if (searchOptions.facets) {
    for (let facet of searchOptions.facets) {
      if (facet) {
        queryParts.push(`${facetTermsParamName}=${facet.id}:${facet.values}`);
      }
    }
  }

  if (searchOptions.brandId) {
    queryParts.push(`facetTerms=BrandId:"${searchOptions.brandId}"`);
  }

  if (searchOptions.keyword) {
    queryParts.push(`${keywordParamName}=${searchOptions.keyword}`);
  }

  if (searchOptions.sortOrder) {
    queryParts.push(`${sortOrderParamName}=${searchOptions.sortOrder}`);
  }

  if (searchOptions.pageSize) {
    queryParts.push(`${pageSizeParamName}=${searchOptions.pageSize}`);
  }

  if (searchOptions.page) {
    queryParts.push(`${pageParamName}=${searchOptions.page}`);
  }

  if (searchOptions.userToken) {
    queryParts.push(`${userTokenParamName}=${searchOptions.userToken}`);
  }

  const searchQuery = queryParts.join('&');

  return searchQuery;
}

/*
 * @param fnDataProcessing is used to process search data using some state dependencies (e.g. to accumulate paginated data)
 */
export function useSearch<TResult>(
  searchQuery: string,
  searchApiSettings: SearchApiSettings,
  initialData: any,
  fnDataProcessing: (data: any) => Promise<TResult> = null,
  deps: DependencyList,
) {
  return useAsyncData(
    async (signal) => {
      const results = await getSearchResults(searchQuery, signal, searchApiSettings);

      if (fnDataProcessing) {
        return fnDataProcessing(results);
      }

      return Promise.resolve(results) as Promise<TResult>;
    },
    initialData,
    deps,
  );
}
